// What a tasting does at the edge: grows its flight, wraps it in a session, invites people, begins,
// and sends each wine's note. Every function reads and writes the run in store.ts, so a screen
// only has to call one and render the result. A solo tasting never leaves the device until a note
// is saved; sharing it (a link, a QR, a friend) is the moment a flight comes into being.
import type { FlightState, WineIdentity } from "@proef/contract";
import type { TastingSessionView } from "../social/tastingSession";
import {
  addFlightWine,
  createFlight,
  deleteTasting,
  EdgeApiError,
  saveTasting,
  startFlight,
  submitFlightNote,
} from "../edgeApi";
import { normaliseQuote } from "@proef/domain";
import { track } from "../learn/analytics";
import { draftToNote } from "../noteDraft";
import {
  attachSessionFlight,
  createSession,
  inviteToSession,
  startSession,
  type InviteRequest,
} from "../social/sessionsApi";
import {
  addWine,
  adoptFlight,
  markQueued,
  markSaved,
  setFlight,
  type RunWine,
  type TastingRun,
} from "./run";
import { clearRun, getRun, updateRun } from "./store";
import { recallLook } from "./wineLook";

function requireRun(): TastingRun {
  const run = getRun();
  if (run === undefined) throw new Error("there is no tasting in progress");
  return run;
}

/** Puts the flight's picture in the run: the one place a flight's state turns into the table. */
export function takeFlight(flight: FlightState): void {
  updateRun((run) => adoptFlight(run, flight, recallLook));
}

/** Adds a wine to the table; once the tasting is shared, to the flight first, so everyone has it. */
export async function addWineToRun(input: {
  wine?: WineIdentity | undefined;
  blind: boolean;
}): Promise<void> {
  const run = requireRun();
  if (run.flightCode === undefined) {
    updateRun((current) =>
      addWine(current, {
        ...(input.wine === undefined ? {} : { wine: input.wine }),
        blind: input.blind,
        recall: recallLook,
      }),
    );
    return;
  }
  if (input.wine === undefined) {
    throw new Error("a blind pour needs its bottle before the tasting can be shared");
  }
  takeFlight(await addFlightWine(run.flightCode, { wine: input.wine }));
}

/**
 * Makes the tasting something others can join: a flight from the wines held so far (even none),
 * and with `session` also a session around it, which is what friends and groups are invited to.
 */
export async function shareRun(options: {
  session: boolean;
  hostName?: string | undefined;
}): Promise<void> {
  let run = requireRun();
  if (run.flightCode === undefined) {
    const bottles = run.wines.flatMap((w) => (w.wine === undefined ? [] : [{ wine: w.wine }]));
    if (bottles.length !== run.wines.length) {
      throw new Error("a blind pour needs its bottle before the tasting can be shared");
    }
    const flight = await createFlight({
      title: run.title,
      blind: run.blind,
      ...(options.hostName === undefined ? {} : { hostDisplayName: options.hostName }),
      wines: bottles,
    });
    takeFlight(flight);
    run = requireRun();
  }
  if (options.session && run.sessionId === undefined && run.flightCode !== undefined) {
    const session = await createSession({ name: run.title });
    await attachSessionFlight(session.id, run.flightCode);
    updateRun((current) =>
      setFlight(current, { flightCode: run.flightCode ?? "", sessionId: session.id }),
    );
  }
}

/** Invites friends or a group, making the session around the tasting first when there is none. */
export async function inviteToRun(
  request: InviteRequest,
  hostName?: string,
): Promise<TastingSessionView> {
  await shareRun({ session: true, hostName });
  const sessionId = requireRun().sessionId;
  if (sessionId === undefined) throw new Error("the tasting has no session to invite people to");
  return inviteToSession(sessionId, request);
}

/** The host presses "begin": the flight starts, the session goes live, notes are taken. */
export async function beginTasting(): Promise<void> {
  const run = requireRun();
  if (run.host && run.flightCode !== undefined && !run.started) {
    takeFlight(await startFlight(run.flightCode));
  }
  if (run.host && run.sessionId !== undefined) {
    try {
      await startSession(run.sessionId);
    } catch (error) {
      // A session that is already live (another device began it) is just as good.
      if (!(error instanceof EdgeApiError && error.status === 409)) throw error;
    }
  }
  updateRun((current) => ({ ...current, started: true }));
}

export type SaveOutcome =
  | { kind: "saved"; tastingId: string | undefined }
  | { kind: "queued" }
  | { kind: "incomplete" };

/** The grape and country a taster called before the reveal; nothing when both are empty. */
function guessOf(wine: RunWine): { grape?: string; country?: string } | undefined {
  const grape = wine.guess.grape.trim();
  const country = wine.guess.country.trim().toUpperCase();
  if (grape === "" && country === "") return undefined;
  return { ...(grape === "" ? {} : { grape }), ...(country === "" ? {} : { country }) };
}

/** Sends one wine's note where it belongs: its flight when shared, the journal otherwise. */
async function send(run: TastingRun, wine: RunWine): Promise<{ tastingId: string | undefined }> {
  const note = draftToNote(wine.draft);
  if (note === undefined) throw new Error("the note has no verdict yet");
  const blindGuess = wine.blind ? guessOf(wine) : undefined;
  if (run.flightCode !== undefined && wine.flightWineId !== undefined) {
    // The line is only ever shown to the people of a session, so a flight without one keeps it.
    const groupQuote = run.sessionId === undefined ? undefined : normaliseQuote(wine.quote);
    await submitFlightNote(run.flightCode, {
      flightWineId: wine.flightWineId,
      note,
      depth: run.depth,
      ...(blindGuess === undefined ? {} : { blindGuess }),
      ...(groupQuote === undefined ? {} : { groupQuote }),
    });
    return { tastingId: undefined };
  }
  const tasting = await saveTasting({
    ...(wine.wine === undefined ? {} : { wine: wine.wine }),
    note,
    depth: run.depth,
    blind: wine.blind,
    ...(blindGuess === undefined ? {} : { blindGuess }),
    ...(wine.tastedAt === undefined ? {} : { tastedAt: wine.tastedAt }),
  });
  // An edited note replaces the one it was reopened from only now that its copy is safely saved; if
  // the old one cannot be removed the journal holds both, which loses nothing.
  if (wine.replaces !== undefined) await deleteTasting(wine.replaces).catch(() => undefined);
  return { tastingId: tasting.id };
}

/** `fetch` rejects with a TypeError when nothing answered; an answer from the edge is for the person. */
const isOffline = (error: unknown): boolean => error instanceof TypeError;

export async function saveRunWine(key: string): Promise<SaveOutcome> {
  const run = requireRun();
  const wine = run.wines.find((w) => w.key === key);
  if (wine === undefined) throw new Error("that wine is not on the table");
  if (draftToNote(wine.draft) === undefined) return { kind: "incomplete" };
  try {
    const { tastingId } = await send(run, wine);
    updateRun((current) => markSaved(current, key, { tastingId }));
    // A solo note on its own is counted when the tasting ends, once its lesson, if any, is known.
    if (!(run.wines.length === 1 && run.flightCode === undefined)) {
      track("note_saved", { depth: run.depth, blind: wine.blind });
    }
    return { kind: "saved", tastingId };
  } catch (error) {
    if (!isOffline(error)) throw error;
    updateRun((current) => markQueued(current, key));
    return { kind: "queued" };
  }
}

let flushing = false;

/** Sends the notes that waited for a connection; one that fails again stays queued. */
export async function flushQueued(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    for (const wine of getRun()?.wines.filter((w) => w.queued) ?? []) {
      const run = getRun();
      if (run === undefined) return;
      try {
        const { tastingId } = await send(run, wine);
        updateRun((current) => markSaved(current, wine.key, { tastingId }));
        track("note_saved", { depth: run.depth, blind: wine.blind });
        // A tasting the taster already left is kept only for this note: now it can go.
        if (getRun()?.finished === true && getRun()?.wines.every((w) => w.saved && !w.queued)) {
          clearRun();
          return;
        }
      } catch (error) {
        // Still offline: the rest would fail the same way. Anything else is for the next attempt.
        if (isOffline(error)) return;
      }
    }
  } finally {
    flushing = false;
  }
}
